import React, { useState, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';

interface ChartDataItem {
  label: string,
  series: {
    data: number[],
    name: string
  }[],
  type: string,
  xAxis: string[]
  yAxisTicks: { name: string, value: number }[]
}

export type EventCardProps = {
  data: ChartDataItem[],
  yesP: number,
  noP: number,
};
const PercentageChart: React.FC<EventCardProps> = ({ ...props }) => {
  const { yesP, noP } = props
  const chartData: ChartDataItem[] = props.data
  const [selectedPeriod, setSelectedPeriod] = useState<string>('1D');

  // 获取当前选中的时间段数据
  const currentData = useMemo(() => {
    return chartData.find(item => item.label === selectedPeriod);
  }, [selectedPeriod, chartData]);

  // 生成ECharts配置 - 完全按照图片样式
  const getOption = (): EChartsOption => {
    if (!currentData) {
      return {};
    }

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ddd',
        borderWidth: 1,
        textStyle: {
          color: '#333'
        },
        formatter: (params: any) => {
          const paramArray = Array.isArray(params) ? params : [params];
          const date = currentData.xAxis[paramArray[0].dataIndex];
          let result = `<div style="font-size: 12px; color: #666; margin-bottom: 8px;">${date}</div>`;
          paramArray.forEach((item: any) => {
            const color = item.seriesName === 'Yes' ? '#ffc107' : '#f44336';
            result += `
              <div style="display: flex; align-items: center; justify-content: space-between; margin: 4px 0; min-width: 120px;">
                <div style="display: flex; align-items: center;">
                  <span style="display: inline-block; width: 8px; height: 8px; background: ${color}; border-radius: 50%; margin-right: 8px;"></span>
                  <span style="font-size: 12px;">${item.seriesName}</span>
                </div>
                <span style="font-weight: bold; font-size: 12px;">${item.value}%</span>
              </div>
            `;
          });
          return result;
        }
      },
      legend: {
        show: false // 图片中没有图例，我们在顶部自定义显示
      },
      grid: {
        left: '1%',
        right: '1%',
        bottom: '5%',
        top: '5%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        // boundaryGap: false,
        data: currentData.xAxis,
        // axisLine: {
        //   lineStyle: {
        //     color: '#e0e0e0'
        //   }
        // },
        // axisTick: {
        //   show: false
        // },
        // axisLabel: {
        //   color: '#666',
        //   fontSize: 10,
        //   interval: (index: number) => {
        //     // 根据时间段动态调整标签显示间隔
        //     const total = currentData.xAxis.length;
        //     if (selectedPeriod === '1D') return index % 12 === 0; // 1D显示部分标签
        //     if (selectedPeriod === '1W') return index % 3 === 0;   // 1W显示部分标签
        //     return index % 15 === 0; // 1M显示部分标签
        //   }
        // },
        // splitLine: {
        //   show: false
        // }
      },
      yAxis: {
        type: 'value',
        min: 0, // currentData.yAxisTicks[0].value,
        max: 100, //currentData.yAxisTicks[currentData.yAxisTicks.length - 1].value,
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#666',
          fontSize: 10,
          formatter: '{value}',
        },
        splitLine: {
          lineStyle: {
            color: '#666',
            type: 'solid'
          }
        }
      },
      series: [
        {
          name: currentData.series[0].name,
          type: 'line',
          data: currentData.series[0].data,
          smooth: true,
          symbol: 'circle',
          symbolSize: 0,
          lineStyle: {
            width: 2,
            color: '#ffc107'
          },
          itemStyle: {
            color: '#ffc107'
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(255, 193, 7, 0.3)' },
                { offset: 1, color: 'rgba(255, 193, 7, 0.05)' }
              ]
            }
          }
        },
        {
          name: currentData.series[1].name,
          type: 'line',
          data: currentData.series[1].data,
          smooth: true,
          symbol: 'circle',
          symbolSize: 0,
          lineStyle: {
            width: 2,
            color: '#f44336'
          },
          itemStyle: {
            color: '#f44336'
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(244, 67, 54, 0.3)' },
                { offset: 1, color: 'rgba(244, 67, 54, 0.05)' }
              ]
            }
          }
        }
      ]
    };
  };

  return (
    <div style={{
      width: '100%',
      maxWidth: '800px',
      margin: '10px auto 0',
    }}>
      <div className='items-center flex justify-between mx-4'>
        {/* 顶部百分比显示 - 完全按照图片样式 */}
        <div className='flex items-center gap-12'>
          <div className='flex items-center gap-4' >
            <div
              className='w-12 h-12 rounded-half'
              style={{ backgroundColor: '#ffc107' }}>
            </div>
            <span className='text-12 text-bravve-sub-800'>
              Yes {yesP}%
            </span>
          </div>
          <div className='flex items-center gap-8'>
            <div
              className='w-12 h-12 rounded-half'
              style={{ backgroundColor: '#f44336' }}></div>
            <span className='text-12 text-bravve-sub-800'>
              No {noP}%
            </span>
          </div>
        </div>

        {/* 时间段切换按钮 - 按照图片样式 */}
        <div 
          className='flex flex-center gap-4 p-4 rounded-20' 
          style={{width: 'fit-content'}}
        >
          {chartData.map((item) => (
            <button
              key={item.label}
              onClick={() => setSelectedPeriod(item.label)}
              style={{
                padding: '6px 12px',
                border: 'none',
                borderRadius: '12px',
                backgroundColor: selectedPeriod === item.label ? '#fff' : 'transparent',
                color: selectedPeriod === item.label ? '#333' : '#666',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: selectedPeriod === item.label ? '600' : '400',
                transition: 'all 0.3s',
                boxShadow: selectedPeriod === item.label ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
      {/* 图表容器 */}
      <ReactECharts
        option={getOption()}
        style={{ height: '300px', width: '100%' }}
        opts={{ renderer: 'svg' }}
      />
    </div>
  );
};

export default PercentageChart;